import React from "react";

import {
  useBulkGoLivePropertyBilling,
  useGoLivePropertyBilling,
} from "manager/hooks/api";
import { Property } from "manager/interfaces/api";
import { NOTIFICATIONS } from "shared/config/constants";
import useModal from "shared/hooks/useModal";
import { openNotification } from "shared/utils/ui";

import {
  BILLING_STATUS,
  billingLiveAtApproximateFromGoNow,
  bulkGoLiveExpected,
  bulkGoLiveResultMessage,
  canSetLive,
  expectedBillingSnapshot,
} from "./billingDisplay";
import SetLiveModal from "./SetLiveModal";

interface SetLiveModalContainerProps {
  properties: Property[];
  onSuccess?: () => void;
}

const SetLiveModalContainer = ({
  properties,
  onSuccess,
}: SetLiveModalContainerProps) => {
  const { closeActiveModalDialog } = useModal();
  const { goLivePropertyBilling, isSaving: isSavingOne } =
    useGoLivePropertyBilling();
  const { bulkGoLivePropertyBilling, isSaving: isSavingBulk } =
    useBulkGoLivePropertyBilling();

  const submit = async ({ billingLiveAt, reason, goNow }) => {
    const eligible = properties.filter(canSetLive);
    const restarting = eligible.some(
      (property) => property.billingStatus === BILLING_STATUS.notLive
    );
    const billingLiveAtApproximate = billingLiveAtApproximateFromGoNow(
      goNow,
      restarting
    );
    if (properties.length === 1 && eligible.length === 1) {
      const property = properties[0];
      await goLivePropertyBilling({
        propertyId: property.id,
        payload: {
          billingLiveAt,
          billingLiveAtApproximate,
          ...(reason ? { reason } : {}),
          ...expectedBillingSnapshot(property),
        },
      });
      openNotification(`${property.name} is set live.`, NOTIFICATIONS.info);
    } else {
      // Send the full selection so the API can skip scheduled rows and list them.
      const result = await bulkGoLivePropertyBilling({
        propertyIds: properties.map((property) => property.id),
        billingLiveAt,
        billingLiveAtApproximate,
        ...(reason ? { reason } : {}),
        // Same loaded snapshot as single-row go-live; bulk lists stale
        // rows instead of 409ing the whole selection.
        expected: bulkGoLiveExpected(properties),
      });
      openNotification(
        bulkGoLiveResultMessage(result, properties),
        NOTIFICATIONS.info
      );
    }
    closeActiveModalDialog();
    onSuccess?.();
  };

  return (
    <SetLiveModal
      properties={properties}
      submit={submit}
      submitting={isSavingOne || isSavingBulk}
    />
  );
};

export default SetLiveModalContainer;
